// The Store OS plans, in one place: the website, the signup page, checkout (Stripe), the billing
// page and the server's limits all read these. Prices are per location, per month, in USD.
// "Users" are named people (the owner counts), per location: a second location brings another
// plan's worth of people.

export type PlanId = 'starter' | 'growth' | 'business';

/** What a plan unlocks beyond the basics every plan has (sales, repairs, inventory, customers, daily reports, staff codes, every industry setup). */
export type PlanFeature =
  | 'tech_assignment' // technician assignment and ready-by times on repairs
  | 'visit_booking' // store visit booking
  | 'pnl' // profit and loss, and margin reports
  | 'special_orders' // special orders for customers
  | 'buybacks' // buying phones and trade-ins
  | 'company_reports' // company-wide reports across locations
  | 'transfers' // stock transfers between locations
  | 'audit_log'; // the activity log

export interface Plan {
  id: PlanId;
  name: string;
  /** Per location, per month, in cents. */
  price_cents: number;
  /** Named users (the owner counts), per location. */
  users_per_location: number;
  features: PlanFeature[];
  /** Stripe's name for this price, so checkout finds it in any Stripe account. */
  lookup_key: string;
}

const GROWTH: PlanFeature[] = ['tech_assignment', 'visit_booking', 'pnl', 'special_orders', 'buybacks'];

export const PLANS: Record<PlanId, Plan> = {
  starter: { id: 'starter', name: 'Starter', price_cents: 6_999, users_per_location: 3, features: [], lookup_key: 'storeos_starter_monthly' },
  growth: { id: 'growth', name: 'Growth', price_cents: 11_999, users_per_location: 10, features: GROWTH, lookup_key: 'storeos_growth_monthly' },
  business: { id: 'business', name: 'Business', price_cents: 19_999, users_per_location: 20, features: [...GROWTH, 'company_reports', 'transfers', 'audit_log'], lookup_key: 'storeos_business_monthly' },
};

export const PLAN_IDS: PlanId[] = ['starter', 'growth', 'business'];

export const FEATURE_LABELS: Record<PlanFeature, string> = {
  tech_assignment: 'Technician assignment and ready-by times',
  visit_booking: 'Store visit booking',
  pnl: 'Profit and loss, and margin reports',
  special_orders: 'Special orders',
  buybacks: 'Buying phones and trade-ins',
  company_reports: 'Company-wide reports',
  transfers: 'Stock transfers between locations',
  audit_log: 'Activity log',
};

/** Most locations one signup can start with (more can be added from Billing). */
export const MAX_LOCATIONS = 25;

export function isPlanId(v: unknown): v is PlanId {
  return typeof v === 'string' && (PLAN_IDS as string[]).includes(v);
}

/** The cheapest plan that has a feature. */
export function planWith(feature: PlanFeature): Plan {
  return PLAN_IDS.map((id) => PLANS[id]).find((p) => p.features.includes(feature)) ?? PLANS.business;
}

/** "$69.99" */
export function planPrice(p: Plan): string {
  return `$${(p.price_cents / 100).toFixed(2)}`;
}

/** The monthly total for a plan and a number of locations, in cents. */
export function monthlyTotal(plan: PlanId, locations: number): number {
  return PLANS[plan].price_cents * Math.max(1, Math.floor(locations));
}

/** Named users allowed for a plan with this many locations. */
export function usersAllowed(plan: PlanId, locations: number): number {
  return PLANS[plan].users_per_location * Math.max(1, Math.floor(locations));
}
